import { Navigate, useLocation } from "react-router-dom";
import { getAuthItem } from "./utils/auth";
import { useAppPermissions } from "./context/PermissionContext";

/** @typedef {'accounting'|'admin'|'client'|'shift'|'personnel'} AppModule */

// Helper to decode JWT and check impersonation status
function decodeJwt(token) {
  try {
    if (!token || token === 'null' || token === 'undefined') return null;
    const payload = token.split('.')[1];
    if (!payload) return null;
    return JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
  } catch (e) {
    return null;
  }
}

function moduleAllowed(requiredModule, perms) {
  if (!requiredModule) return true;
  switch (requiredModule) {
    case "personnel":
      return perms.canAccessPersonnel;
    case "client":
      return perms.canReadClients;
    case "shift":
      return perms.canReadShifts;
    case "accounting":
      return perms.canReadAccounting;
    case "admin":
      return perms.canReadAdmin;
    default:
      return true;
  }
}

export default function ProtectedRoute({
  children,
  adminOnly = false,
  requiredModule = null,
}) {
  const location = useLocation();
  const token = getAuthItem("token");
  const perms = useAppPermissions();
  const forcePasswordChange = getAuthItem("forcePasswordChange") === "true";

  // Filter out invalid tokens
  const validToken = token && token !== 'null' && token !== 'undefined' && token.trim() !== '' ? token : null;

  // Decode token to check for impersonation and admin status
  const tokenPayload = validToken ? decodeJwt(validToken) : null;
  const isImpersonated = tokenPayload?.impersonated === true;
  const impersonatedBy = tokenPayload?.impersonated_by;

  // Check user type from token first, then storage fallback.
  const userTypeFromToken = String(tokenPayload?.type || "").toLowerCase();
  const userTypeFromStorage = String(getAuthItem("userType") || "").toLowerCase();
  const userType = userTypeFromToken || userTypeFromStorage;

  // Check admin status from both token payload (source of truth) and localStorage (for backwards compatibility).
  const isAdminFromToken = tokenPayload?.is_admin === true || tokenPayload?.type === 'admin';
  const isAdminFromStorage = getAuthItem("isAdmin") === "true";
  const isAdmin = isAdminFromToken || isAdminFromStorage;

  // Internal staff types that should access admin module routes.
  const isInternalStaff =
    userType === "admin" ||
    userType === "officestaff" ||
    userType === "manager";

  // For admin-only routes: allow internal staff, or impersonated sessions.
  const canAccessAdminRoute = isInternalStaff || isImpersonated;

  console.log("ProtectedRoute:", {
    hasToken: !!validToken,
    isAdmin,
    isAdminFromToken,
    isAdminFromStorage,
    isImpersonated,
    impersonatedBy,
    userType,
    isInternalStaff,
    adminOnly,
    canAccessAdminRoute,
    tokenPayload: tokenPayload ? { account_id: tokenPayload.account_id, type: tokenPayload.type, is_admin: tokenPayload.is_admin } : null
  });

  if (!validToken) {
    console.log("No valid token found, redirecting to login");
    return <Navigate to="/login" replace />;
  }

  if (forcePasswordChange && location.pathname !== "/change-password") {
    return <Navigate to="/change-password" replace />;
  }

  if (adminOnly && !canAccessAdminRoute) {
    console.log("User is not admin and not impersonating, redirecting to register");
    return <Navigate to="/register" replace />;
  }

  if (adminOnly && requiredModule) {
    if (!perms.loaded) {
      return null;
    }
    if (!moduleAllowed(requiredModule, perms)) {
      return <Navigate to="/dashboard" replace />;
    }
  }

  console.log("Access granted");
  return children;
}
